import React from "react";
import { GhlContactResultsAttachment } from "../types";

interface ContactResultCardProps {
  data: GhlContactResultsAttachment;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

const avatarTint = (i: number): React.CSSProperties => {
  const variants: React.CSSProperties[] = [
    { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
    { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
    {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
    },
  ];
  return variants[i % variants.length];
};

const headerIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)",
};

const tagPillStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
  color: "var(--color-primary)",
};

export const ContactResultCard: React.FC<ContactResultCardProps> = ({ data }) => {
  const { contacts } = data;

  return (
    <div className="my-1 w-full max-w-full overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between border-b border-theme-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={headerIconStyle}>
            <span className="material-icons-outlined text-[18px] text-theme-primary">person_search</span>
          </span>
          <p className="text-[13px] font-bold text-theme-fg">
            {contacts.length} {contacts.length === 1 ? "Contact Match" : "Contact Matches"}
          </p>
        </div>
      </div>

      <div className="flex max-h-[360px] flex-col gap-1.5 overflow-y-auto p-3">
        {contacts.length === 0 && (
          <p className="px-2 py-6 text-center text-[12.5px] text-theme-fg-muted">
            No contacts found matching the search.
          </p>
        )}
        {contacts.map((c, i) => (
          <div
            key={c.contact_id}
            className="flex items-center gap-3 rounded-xl border border-theme-border px-3 py-2"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold" style={avatarTint(i)}>
              {initials(c.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[13px] font-bold text-theme-fg">
                {c.name || "(no name)"}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-3 text-[11.5px] text-theme-fg-muted">
                {c.email && (
                  <span className="inline-flex items-center gap-1">
                    <span className="material-icons-outlined text-[13px]">mail</span>
                    {c.email}
                  </span>
                )}
                {c.phone && (
                  <span className="inline-flex items-center gap-1">
                    <span className="material-icons-outlined text-[13px]">call</span>
                    {c.phone}
                  </span>
                )}
              </div>
            </div>
            {c.tags.length > 0 && (
              <span
                className="flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
                style={tagPillStyle}
              >
                {c.tags[0]}
                {c.tags.length > 1 ? ` +${c.tags.length - 1}` : ""}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
